import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Swords, Plus, Trash2, IndianRupee, Filter, Upload, Download, ArrowRight } from "lucide-react";
import { toast } from "sonner";

import { v2Api } from "@/lib/v2Api";
import { confirm } from "@/lib/confirm";
import AskWidget from "@/components/v2/AskWidget";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const ROLE_OPTIONS = [
  { value: "won_l1", label: "Won (L1)" },
  { value: "l2_l3", label: "Runner-up" },
  { value: "quoted", label: "Quoted" },
  { value: "list", label: "List price" },
];

function ScrapeCsvDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { if (open) { setFile(null); setResult(null); } }, [open]);

  const downloadTemplate = async () => {
    try {
      const tpl = await v2Api.competitors.scrapeTemplate();
      const cols = tpl.columns;
      const example = tpl.example || {};
      const csv = [
        cols.join(","),
        cols.map((c) => {
          const v = (example[c] ?? "").toString().replace(/"/g, '""');
          return /[",\n]/.test(v) ? `"${v}"` : v;
        }).join(","),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "competitor-prices-template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Template fetch failed");
    }
  };

  const upload = async () => {
    if (!file) { toast.error("Pick a CSV first"); return; }
    setBusy(true); setResult(null);
    try {
      const r = await v2Api.competitors.scrapeCsv(file);
      setResult(r);
      toast.success(`${r.rows_added} added · ${r.rows_skipped} skipped`);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Bulk-ingest competitor prices (CSV)</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <p className="text-slate-600">
            Drop a CSV exported from any portal, an award-letter PDF parser, or
            a market-data partner. Rows land in the same{" "}
            <code className="text-xs bg-slate-100 px-1 rounded">competitor_price_observation</code>{" "}
            table that the Suggested-Price panel reads. Required columns:{" "}
            <code className="text-xs">competitor</code>{" "}
            (or{" "}
            <code className="text-xs">competitor_id</code>) +{" "}
            <code className="text-xs">unit_price_inr</code>{" "}
            or{" "}
            <code className="text-xs">total_price_inr</code>.
          </p>
          <Button variant="outline" size="sm" onClick={downloadTemplate}>
            <Download className="w-4 h-4 mr-1" /> Download template
          </Button>
          <Input type="file" accept=".csv"
                 onChange={(e) => setFile(e.target.files?.[0] || null)} />
          {result && (
            <div className="text-xs bg-slate-50 border rounded p-2 space-y-1">
              <p>
                <strong>{result.rows_added}</strong> added ·{" "}
                <strong>{result.rows_skipped}</strong> skipped
              </p>
              {result.warnings?.length > 0 && (
                <ul className="list-disc list-inside text-rose-600 max-h-32 overflow-y-auto">
                  {result.warnings.slice(0, 20).map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={upload} disabled={busy || !file}>
            {busy ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewCompetitorDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ name: "", aliases: "", sector_tags: "", notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ name: "", aliases: "", sector_tags: "", notes: "" }); }, [open]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      await v2Api.competitors.create({
        name: form.name.trim(),
        aliases: form.aliases.split(",").map((s) => s.trim()).filter(Boolean),
        sector_tags: form.sector_tags.split(",").map((s) => s.trim()).filter(Boolean),
        notes: form.notes.trim() || null,
      });
      toast.success("Competitor added");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Add competitor</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <div>
            <Label className="text-xs text-slate-500">Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Forbes Marshall" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Aliases (comma-separated)</Label>
            <Input value={form.aliases} onChange={(e) => setForm({ ...form, aliases: e.target.value })} placeholder="forbes, fme, forbes marshall ltd" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Sector tags (comma-separated)</Label>
            <Input value={form.sector_tags} onChange={(e) => setForm({ ...form, sector_tags: e.target.value })} placeholder="cems, flow, analytical" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Notes</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy}>{busy ? "Saving…" : "Add"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewObservationDialog({ open, onOpenChange, competitors, onSaved }) {
  const blank = () => ({
    competitor_id: competitors[0]?.id || "",
    product_category: "",
    unit_price_inr: "",
    total_price_inr: "",
    qty: "",
    observed_role: "won_l1",
    client_state: "",
    tender_portal: "",
    observed_on: "",
    notes: "",
  });
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(blank()); /* eslint-disable-next-line */ }, [open, competitors]);

  const submit = async () => {
    if (!form.competitor_id) { toast.error("Pick a competitor"); return; }
    if (!form.unit_price_inr && !form.total_price_inr) {
      toast.error("Provide unit or total price"); return;
    }
    setBusy(true);
    try {
      await v2Api.competitors.addObservation({
        competitor_id: form.competitor_id,
        product_category: form.product_category.trim() || null,
        unit_price_inr: form.unit_price_inr ? Number(form.unit_price_inr) : null,
        total_price_inr: form.total_price_inr ? Number(form.total_price_inr) : null,
        qty: form.qty ? Number(form.qty) : null,
        observed_role: form.observed_role,
        client_state: form.client_state.trim() || null,
        tender_portal: form.tender_portal.trim() || null,
        observed_on: form.observed_on || null,
        notes: form.notes.trim() || null,
      });
      toast.success("Observation added");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Save failed");
    } finally { setBusy(false); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Log a competitor price observation</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2 text-sm">
          <div className="col-span-2">
            <Label className="text-xs text-slate-500">Competitor *</Label>
            <select value={form.competitor_id} onChange={set("competitor_id")}
                    className="w-full px-2 py-1 border rounded-md bg-white text-sm">
              {competitors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-slate-500">Product category (free text)</Label>
            <Input value={form.product_category} onChange={set("product_category")} placeholder="CEMS / DAS / FLOW" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Unit price (INR)</Label>
            <Input type="number" value={form.unit_price_inr} onChange={set("unit_price_inr")} />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Total price (INR)</Label>
            <Input type="number" value={form.total_price_inr} onChange={set("total_price_inr")} />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Qty</Label>
            <Input type="number" value={form.qty} onChange={set("qty")} />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Role</Label>
            <select value={form.observed_role} onChange={set("observed_role")}
                    className="w-full px-2 py-1 border rounded-md bg-white text-sm">
              {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Client state code</Label>
            <Input value={form.client_state} onChange={set("client_state")} placeholder="MP / 23" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Portal</Label>
            <Input value={form.tender_portal} onChange={set("tender_portal")} placeholder="gem / cppp" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Observed on</Label>
            <Input type="date" value={form.observed_on} onChange={set("observed_on")} />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-slate-500">Notes</Label>
            <Input value={form.notes} onChange={set("notes")} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy}>{busy ? "Saving…" : "Add"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CompetitorsPage() {
  const [competitors, setCompetitors] = useState([]);
  const [observations, setObservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openComp, setOpenComp] = useState(false);
  const [openObs, setOpenObs] = useState(false);
  const [openScrape, setOpenScrape] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const [filterCompetitor, setFilterCompetitor] = useState("");
  // Sprint #7: sector filter chip + search box. Persisted in localStorage.
  const [sectorFilter, setSectorFilter] = useState(
    () => localStorage.getItem("competitors.sectorFilter") || "all",
  );
  const [q, setQ] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const [c, o] = await Promise.all([
        v2Api.competitors.list(),
        v2Api.competitors.listObservations(filterCompetitor ? { competitor_id: filterCompetitor } : {}),
      ]);
      setCompetitors(c || []);
      setObservations(o || []);
    } catch { toast.error("Could not load competitors"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filterCompetitor]);

  const removeObs = async (o) => {
    if (!await confirm("Delete this observation?", { title: "Delete observation", confirmLabel: "Delete" })) return;
    try { await v2Api.competitors.removeObservation(o.id); refresh(); }
    catch { toast.error("Delete failed"); }
  };
  const removeComp = async (c) => {
    if (!await confirm(`Delete ${c.name}? This will cascade-delete their price observations.`, { title: "Delete competitor", confirmLabel: "Delete" })) return;
    try { await v2Api.competitors.remove(c.id); refresh(); }
    catch { toast.error("Delete failed"); }
  };
  const fmt = (n) => n == null ? "—" : `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n)}`;

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Swords className="w-5 h-5 text-blue-600" /> Competitors
          </h1>
          <p className="text-sm text-slate-500">
            Track competing OEMs and their winning prices. The pricing-intelligence
            engine on each BoQ line consumes these observations to recommend a
            price band with win-rate.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setOpenScrape(true)}>
            <Upload className="w-4 h-4 mr-1" /> Bulk scrape (CSV)
          </Button>
          <Button variant="outline" onClick={() => setOpenObs(true)} disabled={competitors.length === 0}>
            <IndianRupee className="w-4 h-4 mr-1" /> Log price
          </Button>
          <Button onClick={() => setOpenComp(true)}>
            <Plus className="w-4 h-4 mr-1" /> Add competitor
          </Button>
        </div>
      </div>

      {loading && <p className="text-sm text-slate-500">Loading…</p>}

      {/* Sprint #7: stats banner + search + sector filter chips. The
          existing 2-column card grid stays unchanged. */}
      {!loading && competitors.length > 0 && (() => {
        const sectors = Array.from(
          new Set(competitors.flatMap((c) => c.sector_tags || []).filter(Boolean))
        ).sort();
        return (
          <>
            <Card className="border-[#E2E8F0]">
              <CardContent className="py-3 flex flex-wrap items-center justify-between gap-3 text-xs">
                <div className="flex gap-4">
                  <span><strong className="text-slate-800">{competitors.length}</strong> competitors</span>
                  <span><strong className="text-slate-800">{sectors.length}</strong> sectors</span>
                  <span><strong className="text-slate-800">{observations.length}</strong> observations</span>
                </div>
                <input
                  type="search"
                  placeholder="Search name / alias / notes…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="h-7 text-xs px-2 border rounded w-60"
                />
              </CardContent>
            </Card>
            {sectors.length > 0 && (
              <div className="flex flex-wrap items-center gap-1">
                <button
                  type="button"
                  onClick={() => { setSectorFilter("all"); localStorage.setItem("competitors.sectorFilter", "all"); }}
                  className={`text-[11px] px-2 py-1 rounded border ${sectorFilter === "all" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600"}`}
                >
                  All sectors
                </button>
                {sectors.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => { setSectorFilter(s); localStorage.setItem("competitors.sectorFilter", s); }}
                    className={`text-[11px] px-2 py-1 rounded border ${sectorFilter === s ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </>
        );
      })()}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {competitors
          .filter((c) => {
            if (sectorFilter !== "all" && !(c.sector_tags || []).includes(sectorFilter)) return false;
            if (q) {
              const needle = q.toLowerCase();
              const hay = [
                c.name || "",
                ...(c.aliases || []),
                c.notes || "",
                ...(c.sector_tags || []),
              ].join(" ").toLowerCase();
              return hay.includes(needle);
            }
            return true;
          })
          .map((c) => (
          <Card key={c.id}>
            <CardContent className="py-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 truncate">{c.name}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(c.sector_tags || []).map((t) => (
                      <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>
                    ))}
                  </div>
                  {c.aliases?.length > 0 && (
                    <p className="text-[10px] text-slate-400 mt-1">
                      aliases: {c.aliases.join(", ")}
                    </p>
                  )}
                  {c.notes && <p className="text-xs text-slate-500 mt-1 italic line-clamp-2">{c.notes}</p>}
                </div>
                <div className="text-right shrink-0 text-xs text-slate-500">
                  <p><span className="font-mono font-semibold text-slate-700">{c.observation_count}</span> obs</p>
                  <Link to={`/v2/competitors/${c.id}`}
                        className="text-blue-600 hover:underline mt-1 text-[10px] inline-flex items-center gap-0.5">
                    Details <ArrowRight className="w-3 h-3" />
                  </Link>
                  <br />
                  <button onClick={() => setFilterCompetitor(filterCompetitor === c.id ? "" : c.id)}
                          className="text-blue-600 hover:underline mt-1 text-[10px]">
                    {filterCompetitor === c.id ? "clear" : "view obs"}
                  </button>
                  <br />
                  <button onClick={() => removeComp(c)} className="text-slate-300 hover:text-rose-500 mt-1">
                    <Trash2 className="w-3.5 h-3.5 inline" />
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {observations.length > 0 && (
        <Card>
          <CardContent className="py-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-medium flex items-center gap-1">
                <Filter className="w-3 h-3" />
                Price observations {filterCompetitor ? `(filtered)` : `(${observations.length})`}
              </p>
              {filterCompetitor && (
                <button onClick={() => setFilterCompetitor("")}
                        className="text-[10px] text-blue-600 hover:underline">clear filter</button>
              )}
            </div>
            <table className="w-full text-xs">
              <thead className="text-slate-500 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="text-left py-1">Competitor</th>
                  <th className="text-left py-1">Category</th>
                  <th className="text-left py-1">Role</th>
                  <th className="text-right py-1">Unit</th>
                  <th className="text-right py-1">Total</th>
                  <th className="text-left py-1">State</th>
                  <th className="text-left py-1">Portal</th>
                  <th className="text-left py-1">Date</th>
                  <th className="text-left py-1">Source</th>
                  <th className="text-left py-1"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {observations.map((o) => (
                  <tr key={o.id}>
                    <td className="py-1">{o.competitor_name}</td>
                    <td className="py-1 text-slate-500">{o.product_category || "—"}</td>
                    <td className="py-1 text-[10px]">
                      <Badge variant="outline" className="text-[10px]">{o.observed_role}</Badge>
                    </td>
                    <td className="py-1 text-right font-mono">{fmt(o.unit_price_inr)}</td>
                    <td className="py-1 text-right font-mono">{fmt(o.total_price_inr)}</td>
                    <td className="py-1">{o.client_state || "—"}</td>
                    <td className="py-1">{o.tender_portal || "—"}</td>
                    <td className="py-1 font-mono text-slate-500">{o.observed_on || "—"}</td>
                    <td className="py-1 text-[10px] text-slate-400">{o.source}</td>
                    <td className="py-1">
                      <button onClick={() => removeObs(o)} className="text-slate-300 hover:text-rose-500">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <NewCompetitorDialog open={openComp} onOpenChange={setOpenComp} onSaved={refresh} />
      <NewObservationDialog open={openObs} onOpenChange={setOpenObs} competitors={competitors} onSaved={refresh} />
      <ScrapeCsvDialog open={openScrape} onOpenChange={setOpenScrape} onDone={refresh} />

      <AskWidget
        title="Ask competitors"
        placeholder="Ask about competitors, their projects, certs…"
        examples={[
          "Who has won most CEMS bids in last 12 months?",
          "List competitors with IECEx certification.",
          "Which competitor undercut us on price recently?",
        ]}
        askFn={(q, k) => v2Api.competitors.ask(q, k)}
        onCitationClick={(c) => { if (c.deep_link) window.location.href = c.deep_link; }}
        open={askOpen}
        onOpen={() => setAskOpen(true)}
        onClose={() => setAskOpen(false)}
        buttonColor="bg-orange-600 hover:bg-orange-700"
      />
    </div>
  );
}
